import { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

// Bindings from src/lib/commands/registry.tsx, keyed by modifiers then key.
const ACTIONS: Record<string, string> = {
  j: 'Next thread',
  k: 'Previous thread',
  e: 'Archive',
  'shift+e': 'Unarchive',
  z: 'Undo',
  'shift+z': 'Redo',
  s: 'Star',
  u: 'Mark read or unread',
  x: 'Select',
  c: 'Compose',
  r: 'Reply',
  'shift+r': 'Reply all',
  f: 'Forward',
  n: 'Next message',
  p: 'Previous message',
  '/': 'Search',
  '1': 'Open mail',
  '2': 'Open calendar',
  'shift+1': 'Mark spam',
  'shift+3': 'Trash',
  enter: 'Open thread',
  arrowleft: 'Switch split',
  arrowright: 'Switch split',
  'meta+k': 'Command menu',
  'meta+b': 'Toggle sidebar',
  'meta+a': 'Select all',
  'ctrl+1': 'Switch to mailbox 1',
  'ctrl+2': 'Switch to mailbox 2',
  'ctrl+3': 'Switch to mailbox 3',
};
const DEMO = ['j', 'j', 'e', 'k', 's', 'z', 'meta+k', 'arrowright', 'r', '/', 'ctrl+2', 'c'];
const DEMO_INTERVAL_MS = 1_500;
const PRESS_MS = 420;
const PAUSE_AFTER_INPUT_MS = 5_000;

const MODIFIERS: Modifier[] = ['meta', 'ctrl', 'alt', 'shift'];
const MODIFIER_FROM_KEY: Record<string, Modifier> = {
  meta: 'meta',
  control: 'ctrl',
  alt: 'alt',
  shift: 'shift',
};
// US layout: the base key under each shifted symbol.
const UNSHIFTED: Record<string, string> = {
  '!': '1',
  '@': '2',
  '#': '3',
  $: '4',
  '%': '5',
  '^': '6',
  '&': '7',
  '*': '8',
  '(': '9',
  ')': '0',
  _: '-',
  '+': '=',
  '{': '[',
  '}': ']',
  '|': '\\',
  ':': ';',
  '"': "'",
  '<': ',',
  '>': '.',
  '?': '/',
  '~': '`',
};
const DISPLAY: Record<string, string> = {
  touchid: 'Touch ID',
  meta: '⌘',
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  arrowdown: '↓',
  enter: '↵',
  escape: 'esc',
  space: 'Space',
  backspace: '⌫',
  tab: '⇥',
};

// Apple Magic Keyboard (US). Every row is 14.5 key units wide.
const ROWS: KeySpec[][] = [
  [
    ['escape', 'esc', '', 1.5, 'left'],
    ...Array.from(
      { length: 12 },
      (_, index): KeySpec => [`f${index + 1}`, `F${index + 1}`, '', 1, 'function']
    ),
    ['touchid', '', '', 1, 'function'],
  ],
  [
    ['`', '`', '~', 1, 'center'],
    ...[...'1234567890'].map(
      (digit, index): KeySpec => [digit, digit, '!@#$%^&*()'[index], 1, 'center']
    ),
    ['-', '-', '_', 1, 'center'],
    ['=', '=', '+', 1, 'center'],
    ['backspace', 'delete', '', 1.5, 'right'],
  ],
  [
    ['tab', 'tab', '', 1.5, 'left'],
    ...[...'qwertyuiop'].map((letter): KeySpec => [letter, letter.toUpperCase(), '', 1, 'center']),
    ['[', '[', '{', 1, 'center'],
    [']', ']', '}', 1, 'center'],
    ['\\', '\\', '|', 1, 'center'],
  ],
  [
    ['capslock', 'caps lock', '', 1.75, 'left'],
    ...[...'asdfghjkl'].map((letter): KeySpec => [letter, letter.toUpperCase(), '', 1, 'center']),
    [';', ';', ':', 1, 'center'],
    ["'", "'", '"', 1, 'center'],
    ['enter', 'return', '', 1.75, 'right'],
  ],
  [
    ['shift', 'shift', '', 2.25, 'left'],
    ...[...'zxcvbnm'].map((letter): KeySpec => [letter, letter.toUpperCase(), '', 1, 'center']),
    [',', ',', '<', 1, 'center'],
    ['.', '.', '>', 1, 'center'],
    ['/', '/', '?', 1, 'center'],
    ['shift', 'shift', '', 2.25, 'right'],
  ],
  [
    ['fn', 'fn', '', 1, 'left'],
    ['ctrl', 'control', '⌃', 1, 'left'],
    ['alt', 'option', '⌥', 1, 'left'],
    ['meta', 'command', '⌘', 1.25, 'left'],
    ['space', '', '', 5, 'center'],
    ['meta', 'command', '⌘', 1.25, 'right'],
    ['alt', 'option', '⌥', 1, 'right'],
  ],
];

const KEY_CLASS_NAME =
  'border-gray-7 text-gray-11 hover:bg-gray-4 hover:border-gray-8 active:bg-gray-5 relative flex min-w-0 cursor-pointer flex-col items-center justify-center rounded-[4px] border leading-none transition-colors';
const PRESSED_CLASS_NAME =
  'bg-blue-5 text-blue-11 border-blue-8 hover:bg-blue-5 hover:border-blue-8';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type Modifier = 'meta' | 'ctrl' | 'alt' | 'shift';

/** `[key, legend, shifted legend or modifier symbol, width in key units, legend placement]` */
type KeySpec = [string, string, string, number, 'center' | 'left' | 'right' | 'function'];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/**
 * A Magic Keyboard that plays the app's shortcuts. Typing or clicking keys presses them and shows
 * the matching action; the demo pauses for a few seconds after any input.
 */
const MagicKeyboard: React.FC = () => {
  const keyboard = useRef<HTMLDivElement>(null);
  const [pressed, setPressed] = useState<Set<string>>(() => new Set(['j']));
  const [combo, setCombo] = useState<string[]>(['j']);
  const [action, setAction] = useState<string>(ACTIONS.j);
  const held = useRef<Set<Modifier>>(new Set());
  const pausedUntil = useRef<number>(0);
  const releaseTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const press = useCallback((parts: string[]) => {
    const shortcut = ACTIONS[parts.join('+')];
    clearTimeout(releaseTimer.current);
    setPressed(new Set(parts));
    setCombo(parts);
    setAction(shortcut ?? 'No shortcut assigned to this key');
    releaseTimer.current = setTimeout(() => setPressed(new Set(held.current)), PRESS_MS);
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let step = 0;
    const interval = reduceMotion
      ? undefined
      : setInterval(() => {
          if (Date.now() < pausedUntil.current) return;
          press(DEMO[step++ % DEMO.length].split('+'));
        }, DEMO_INTERVAL_MS);

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName ?? '')) {
        return;
      }
      pausedUntil.current = Date.now() + PAUSE_AFTER_INPUT_MS;
      const modifier = MODIFIER_FROM_KEY[event.key.toLowerCase()];
      if (modifier) {
        held.current.add(modifier);
        setPressed((current) => new Set([...current, modifier]));
        return;
      }
      if (event.repeat) return;
      let key = event.key === ' ' ? 'space' : event.key.toLowerCase();
      let shift = event.shiftKey;
      if (UNSHIFTED[event.key]) {
        key = UNSHIFTED[event.key];
        shift = true;
      }
      const parts = [
        ...(event.metaKey ? ['meta'] : []),
        ...(event.ctrlKey ? ['ctrl'] : []),
        ...(event.altKey ? ['alt'] : []),
        ...(shift ? ['shift'] : []),
        key,
      ];
      // Keep the browser's find-in-page from opening; leave every other key alone.
      if (parts.length === 1 && key === '/') event.preventDefault();
      press(parts);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      const modifier = MODIFIER_FROM_KEY[event.key.toLowerCase()];
      if (!modifier) return;
      held.current.delete(modifier);
      setPressed((current) => {
        const next = new Set(current);
        next.delete(modifier);
        return next;
      });
    };

    const target = keyboard.current;
    target?.addEventListener('keydown', onKeyDown);
    target?.addEventListener('keyup', onKeyUp);
    return () => {
      clearInterval(interval);
      clearTimeout(releaseTimer.current);
      target?.removeEventListener('keydown', onKeyDown);
      target?.removeEventListener('keyup', onKeyUp);
    };
  }, [press]);

  const onKeyClick = (key: string) => {
    pausedUntil.current = Date.now() + PAUSE_AFTER_INPUT_MS;
    if ((MODIFIERS as string[]).includes(key)) {
      // Clicking a modifier latches it until the next key, like sticky keys.
      const modifier = key as Modifier;
      if (held.current.has(modifier)) held.current.delete(modifier);
      else held.current.add(modifier);
      setPressed(new Set(held.current));
      return;
    }
    const parts = [...MODIFIERS.filter((modifier) => held.current.has(modifier)), key];
    held.current.clear();
    press(parts);
  };

  const renderKey = ([key, legend, secondary, width, placement]: KeySpec, index: number) => (
    <button
      key={`${key}-${index}`}
      type="button"
      aria-label={DISPLAY[key] ?? legend ?? key}
      onClick={() => onKeyClick(key)}
      style={{ flex: width }}
      className={clsx(
        KEY_CLASS_NAME,
        placement === 'function' ? 'h-3 text-[6px]' : 'h-[22px]',
        placement === 'center' && 'gap-px text-[9px]',
        (placement === 'left' || placement === 'right') &&
          'justify-end px-[3px] pb-[3px] text-[6.5px]',
        placement === 'left' && 'items-start',
        placement === 'right' && 'items-end',
        key === 'escape' && 'h-3 pb-0.5',
        // Half-height arrows; up and down share one key's height, stacked.
        key.startsWith('arrow') && 'h-[11px] text-[6px]',
        key === 'arrowup' && 'rounded-b-none',
        key === 'arrowdown' && 'rounded-t-none border-t-0',
        pressed.has(key) && PRESSED_CLASS_NAME
      )}
    >
      {secondary && placement === 'center' ? (
        <span className="text-[7px]">{secondary}</span>
      ) : secondary ? (
        <span
          className={clsx(
            'absolute top-0.5 text-[7px] leading-2',
            placement === 'left' ? 'right-[3px]' : 'left-[3px]'
          )}
        >
          {secondary}
        </span>
      ) : null}
      <span>{legend}</span>
    </button>
  );

  return (
    <div
      ref={keyboard}
      tabIndex={0}
      aria-label="Keyboard shortcuts demo"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-[3px]">
        {ROWS.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-[3px]">
            {row.map(renderKey)}
            {rowIndex === ROWS.length - 1 ? (
              <div className="flex min-w-0 items-end gap-[3px]" style={{ flex: 3 }}>
                {renderKey(['arrowleft', '◀', '', 1, 'center'], 0)}
                <div className="flex min-w-0 flex-1 flex-col">
                  {renderKey(['arrowup', '▲', '', 1, 'center'], 0)}
                  {renderKey(['arrowdown', '▼', '', 1, 'center'], 0)}
                </div>
                {renderKey(['arrowright', '▶', '', 1, 'center'], 0)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="flex h-6 items-center gap-2.5">
        <span className="flex gap-1">
          {combo.map((part, index) => (
            <kbd
              key={`${part}-${index}`}
              className="border-gray-6 bg-gray-3 text-gray-12 flex h-6 min-w-6 items-center justify-center rounded-md border px-1.5 font-sans text-xs"
            >
              {DISPLAY[part] ?? part.toUpperCase()}
            </kbd>
          ))}
        </span>
        <span className="text-sm font-medium">{action}</span>
      </div>
    </div>
  );
};

export default MagicKeyboard;
